import type { TranslateFn } from '@/lib/i18n';

import type { Scope1CategoryBucket, Scope2EnergyType } from '../types/activity-data';
import { SCOPE3_CATEGORIES_META } from '../types/ghg-settings';

type GhgTranslateFn = TranslateFn | undefined;

function translateWithFallback(
  t: GhgTranslateFn,
  key: string,
  fallback: string,
  interpolation?: Record<string, unknown>
): string {
  if (!t) return fallback;
  const translated = t(key, { defaultValue: fallback, ...interpolation });
  return translated === key ? fallback : translated;
}

function toTitleCaseLabel(raw: string): string {
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeActivityCategory(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[()/,]+/g, ' ')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

const SCOPE1_BUCKET_TRANSLATIONS: Record<Scope1CategoryBucket, { key: string; fallback: string }> =
  {
    stationary: {
      key: 'ghgScope1.bucket_stationary',
      fallback: 'Stationary Combustion',
    },
    mobile: {
      key: 'ghgScope1.bucket_mobile',
      fallback: 'Mobile Combustion',
    },
    process: {
      key: 'ghgScope1.bucket_process',
      fallback: 'Process Emissions',
    },
    fugitive: {
      key: 'ghgScope1.bucket_fugitive',
      fallback: 'Fugitive Emissions',
    },
    other: {
      key: 'ghgScope1.bucket_other',
      fallback: 'Other',
    },
  };

const SCOPE2_ENERGY_TRANSLATIONS: Record<
  Scope2EnergyType | 'other',
  { key: string; fallback: string }
> = {
  electricity: {
    key: 'gHGOnboardingWizard.scope2_purchased_electricity',
    fallback: 'Purchased Electricity',
  },
  steam: {
    key: 'gHGOnboardingWizard.scope2_purchased_steam',
    fallback: 'Purchased Steam',
  },
  heating: {
    key: 'gHGOnboardingWizard.scope2_purchased_heating',
    fallback: 'Purchased Heating',
  },
  cooling: {
    key: 'gHGOnboardingWizard.scope2_purchased_cooling',
    fallback: 'Purchased Cooling',
  },
  other: {
    key: 'ghgScope1.bucket_other',
    fallback: 'Other',
  },
};

const ACTIVITY_CATEGORY_TRANSLATIONS: Record<
  string,
  { key?: string; fallback?: string; scope3Category?: number }
> = {
  stationary_combustion: {
    key: 'gHGOnboardingWizard.scope1_stationary_combustion',
    fallback: 'Stationary Combustion',
  },
  mobile_combustion: {
    key: 'gHGOnboardingWizard.scope1_mobile_combustion',
    fallback: 'Mobile Combustion',
  },
  process_emissions: {
    key: 'gHGOnboardingWizard.scope1_process_emissions',
    fallback: 'Process Emissions',
  },
  fugitive_emissions: {
    key: 'gHGOnboardingWizard.scope1_fugitive_emissions',
    fallback: 'Fugitive Emissions',
  },
  purchased_electricity: {
    key: 'gHGOnboardingWizard.scope2_purchased_electricity',
    fallback: 'Purchased Electricity',
  },
  purchased_steam: {
    key: 'gHGOnboardingWizard.scope2_purchased_steam',
    fallback: 'Purchased Steam',
  },
  purchased_heat: {
    key: 'gHGOnboardingWizard.scope2_purchased_heating',
    fallback: 'Purchased Heating',
  },
  purchased_heating: {
    key: 'gHGOnboardingWizard.scope2_purchased_heating',
    fallback: 'Purchased Heating',
  },
  purchased_heat_steam: {
    key: 'gHGOnboardingWizard.scope2_purchased_heating',
    fallback: 'Purchased Heating',
  },
  purchased_cooling: {
    key: 'gHGOnboardingWizard.scope2_purchased_cooling',
    fallback: 'Purchased Cooling',
  },
  purchased_goods_and_services: { scope3Category: 1 },
  purchased_goods_services: { scope3Category: 1 },
  capital_goods: { scope3Category: 2 },
  fuel_and_energy_related_activities: { scope3Category: 3 },
  fuel_energy_activities: { scope3Category: 3 },
  upstream_transportation_and_distribution: { scope3Category: 4 },
  upstream_transportation: { scope3Category: 4 },
  waste_generated_in_operations: { scope3Category: 5 },
  waste_operations: { scope3Category: 5 },
  business_travel: { scope3Category: 6 },
  employee_commuting: { scope3Category: 7 },
  upstream_leased_assets: { scope3Category: 8 },
  downstream_transportation_and_distribution: { scope3Category: 9 },
  downstream_transportation: { scope3Category: 9 },
  processing_of_sold_products: { scope3Category: 10 },
  processing_sold_products: { scope3Category: 10 },
  use_of_sold_products: { scope3Category: 11 },
  use_sold_products: { scope3Category: 11 },
  end_of_life_treatment_of_sold_products: { scope3Category: 12 },
  end_of_life_sold_products: { scope3Category: 12 },
  downstream_leased_assets: { scope3Category: 13 },
  franchises: { scope3Category: 14 },
  investments: { scope3Category: 15 },
};

export function getScope1BucketLabel(bucket: Scope1CategoryBucket, t?: GhgTranslateFn): string {
  const translation = SCOPE1_BUCKET_TRANSLATIONS[bucket];
  return translateWithFallback(t, translation.key, translation.fallback);
}

export function getScope2EnergyTypeLabel(
  energyType: Scope2EnergyType | 'other',
  t?: GhgTranslateFn
): string {
  const translation = SCOPE2_ENERGY_TRANSLATIONS[energyType];
  return translateWithFallback(t, translation.key, translation.fallback);
}

export function getScope3CategoryName(categoryNumber: number, t?: GhgTranslateFn): string {
  const meta = SCOPE3_CATEGORIES_META.find((category) => category.number === categoryNumber);
  const fallback = meta?.name ?? `Category ${String(categoryNumber)}`;
  return translateWithFallback(
    t,
    `gHGOnboardingWizard.scope3_c${String(categoryNumber)}`,
    fallback
  );
}

export function getScope3CategoryTitle(categoryNumber: number, t?: GhgTranslateFn): string {
  const label = getScope3CategoryName(categoryNumber, t);
  return translateWithFallback(
    t,
    'ghgScope3.categoryTitle',
    `Scope 3 Category ${String(categoryNumber)}: ${label}`,
    { number: categoryNumber, label }
  );
}

export function getActivityCategoryLabel(
  activityCategory: string,
  t?: GhgTranslateFn,
  options?: { scope3Category?: number | null }
): string {
  const normalized = normalizeActivityCategory(activityCategory);
  if (!normalized) return '';

  const translation = ACTIVITY_CATEGORY_TRANSLATIONS[normalized];
  if (translation?.key && translation.fallback) {
    return translateWithFallback(t, translation.key, translation.fallback);
  }

  const scope3Category = options?.scope3Category ?? translation?.scope3Category;
  if (scope3Category != null) {
    return getScope3CategoryName(scope3Category, t);
  }

  return toTitleCaseLabel(normalized);
}
